import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PreviewTemplate from 'components/common/previewTemplate';
import TemplateStatusTag, { isTemplateSelectable } from 'components/templates/templateStatus';
import AlertDialog from 'components/UI/AlertDialog';
import { PrimaryBtn } from 'components/UI/Buttons';
import useCurrentUser from 'hooks/useCurrentUser';
import CreateTemplateForm from 'pages/Client/CreateTemplateForm';
import SurveyConfigurationSection from 'pages/FollowUpConfig/components/SurveyConfigurationSection';
import FollowUpConfigFormAttributes from 'pages/FollowUpConfig/FollowUpConfigFormAttributes';
import { type FC, useEffect, useState } from 'react';
import { useGetBusinessUnitTemplatesQuery } from 'services/api/businessUnits.api';
import { useCreateFollowUpMutation } from 'services/api/followUpConfig.api';
import { clientAttributes } from 'services/types/client';
import type { FollowUpConfig, FollowUpConfigDraft } from 'services/types/followUpConfig';
import type { TemplateResponse } from 'services/types/message';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

import { bodyTextSx, labelSx, sectionGap, sectionSubtitleSx, sectionTitleSx, surfaceCardSx } from '../styles';

/**
 * Maps a named param to a client attribute based on common parameter names
 */
const getClientAttributeForParam = (param: { param_name: string; example: string }): string => {
  const paramName = param.param_name;
  const lowerParamName = paramName.toLowerCase();

  if (['client', 'cliente', 'nombre', 'nombre_cliente', 'name'].includes(lowerParamName)) {
    return 'client.firstName';
  }
  if (['brand', 'marca', 'brand_of_interest'].includes(lowerParamName)) {
    return 'client.brandOfInterest';
  }
  if (['model', 'modelo', 'auto', 'car', 'model_of_interest'].includes(lowerParamName)) {
    return 'client.modelsOfInterest';
  }
  if (['branch_office', 'sucursal', 'branch'].includes(lowerParamName)) {
    return 'client.branchOffice';
  }
  if (['email', 'correo'].includes(lowerParamName)) {
    return 'client.email';
  }
  if (['phone', 'telefono', 'celular'].includes(lowerParamName)) {
    return 'client.phone';
  }

  // Default: return the param name as placeholder for manual editing
  return param.example;
};

const getTemplateBodyParameters = (template: TemplateResponse[number] | null) => {
  if (!template) return null;
  const bodyComponent = template.components.find((component) => component.type === 'BODY');
  if (!bodyComponent?.example) return null;

  // Handle named params format
  if (bodyComponent.example.body_text_named_params) {
    return JSON.stringify(
      {
        bodyComponents: bodyComponent.example.body_text_named_params.map((param) => ({
          paramName: param.param_name,
          value: getClientAttributeForParam(param),
        })),
      },
      null,
      2
    );
  }

  // Handle positional params format
  if (bodyComponent.example.body_text) {
    return JSON.stringify(
      {
        bodyComponents: bodyComponent.example.body_text[0],
      },
      null,
      2
    );
  }
  return null;
};

interface FollowUpConfigFormProps {
  businessUnitId: number;
  selectFollowUp: (followUp: FollowUpConfig) => void;
}

const FollowUpConfigForm: FC<FollowUpConfigFormProps> = ({ businessUnitId, selectFollowUp }) => {
  const currentUser = useCurrentUser();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateResponse[number] | null>(null);
  const [outboundCampaignId, setOutboundCampaignId] = useState<string | null>(null);
  const [openNewTemplate, setOpenNewTemplate] = useState<boolean>(false);
  const [templateBodyParameters, setTemplateBodyParameters] = useState<{ title: string; value: string }[] | null>(null);

  const [newFollowUpConfig, setNewFollowUpConfig] = useState<FollowUpConfigDraft>({
    businessUnitId,
    templateName: selectedTemplate?.name || null,
    minMinutesWaitTime: 10,
    maxMinutesWaitTime: 129_600, // 3 months
    minWorkingHour: 9,
    maxWorkingHour: 18,
    sendOnWeekend: false,
    identifier: outboundCampaignId || '',
    maxConsecutive: 1,
    maxAmountInConversation: 1,
    previousMessageKeepAliveIdentifier: null,
    requiredPreviousFollowUpsIds: null,
    conditionsToSend: JSON.stringify(
      {
        lead: { outboundCampaignId: outboundCampaignId || '' },
      },
      null,
      2
    ),
    templateComponents: getTemplateBodyParameters(selectedTemplate),
    isActive: true,
    clientsBatchSize: 50,
    functionName: null,
    prompt: null,
    specificTexts: null,
    imageUrl: null,
    templatesToChoose: null,
    surveyConfig: { surveyName: '', question: '', buttons: [] },
  });

  const [isError, setIsError] = useState(false);

  const { data: templates = [], isLoading: isLoadingTemplates } = useGetBusinessUnitTemplatesQuery(businessUnitId, {
    skip: !businessUnitId,
  });
  const [
    createFollowUpConfig,
    { isError: isCreateError, error: createError, isSuccess: isCreateSuccess, data: newFollowUp },
  ] = useCreateFollowUpMutation();

  useEffect(() => {
    if (isCreateError) {
      setIsError(true);
    }
  }, [isCreateError]);

  useEffect(() => {
    if (isCreateSuccess && newFollowUp) {
      selectFollowUp(newFollowUp);
    }
  }, [isCreateSuccess]);

  useEffect(() => {
    setNewFollowUpConfig((prev) => ({
      ...prev,
      templateName: selectedTemplate?.name || null,
      templateComponents: getTemplateBodyParameters(selectedTemplate),
    }));

    // Pre-fill templateBodyParameters for named params templates
    const bodyComp = selectedTemplate?.components.find((c) => c.type === 'BODY');
    const templateNamedParams = bodyComp?.example?.body_text_named_params;

    if (templateNamedParams) {
      // Map named params to clientAttributes options
      const prefilledParams = templateNamedParams.map((param) => {
        const autoValue = getClientAttributeForParam(param);
        // Find matching clientAttribute or create a custom one
        const matchingAttr = clientAttributes.find((attr) => attr.value === autoValue);
        return matchingAttr || { title: param.param_name, value: autoValue };
      });
      setTemplateBodyParameters(prefilledParams);
    } else {
      setTemplateBodyParameters(null);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (templateBodyParameters) {
      const bodyComp = selectedTemplate?.components.find((c) => c.type === 'BODY');
      const templateNamedParams = bodyComp?.example?.body_text_named_params;

      // For named params, keep the paramName from the template
      const bodyComponents = templateNamedParams
        ? templateBodyParameters.map((p, index) => ({
            paramName: templateNamedParams[index]?.param_name || `param_${index}`,
            value: p.value,
          }))
        : templateBodyParameters.map((p) => p.value);

      setNewFollowUpConfig((prev) => ({
        ...prev,
        templateComponents: JSON.stringify({ bodyComponents }),
      }));
    }
  }, [templateBodyParameters, selectedTemplate]);

  useEffect(() => {
    setNewFollowUpConfig((prev) => ({
      ...prev,
      identifier: outboundCampaignId || '',
      conditionsToSend: JSON.stringify(
        {
          lead: { outboundCampaignId: outboundCampaignId || '' },
        },
        null,
        2
      ),
    }));
  }, [outboundCampaignId]);

  const bodyComponent = selectedTemplate?.components.find(
    (c) => c.type === 'BODY' && (c.example?.body_text || c.example?.body_text_named_params)
  );
  const namedParams = bodyComponent?.example?.body_text_named_params;
  const bodyComponentsLength = namedParams?.length || bodyComponent?.example?.body_text?.[0]?.length;

  const handleTemplateBodyParametersChange = (parameters: { title: string; value: string }[]) => {
    if (bodyComponentsLength && parameters.length <= bodyComponentsLength) {
      setTemplateBodyParameters(parameters);
    }
  };

  const numericRangeError = (() => {
    const {
      minMinutesWaitTime,
      maxMinutesWaitTime,
      minWorkingHour,
      maxWorkingHour,
      maxConsecutive,
      maxAmountInConversation,
      clientsBatchSize,
    } = newFollowUpConfig;
    if (minMinutesWaitTime < 1) return 'El tiempo mínimo de espera debe ser mayor o igual a 1';
    if (maxMinutesWaitTime < 11) return 'El tiempo máximo de espera debe ser mayor o igual a 11';
    if (minMinutesWaitTime >= maxMinutesWaitTime) return 'El tiempo mínimo de espera debe ser menor que el máximo';
    if (minWorkingHour < 0 || minWorkingHour > 23) return 'La hora mínima debe estar entre 0 y 23';
    if (maxWorkingHour < 0 || maxWorkingHour > 23) return 'La hora máxima debe estar entre 0 y 23';
    if (minWorkingHour >= maxWorkingHour) return 'La hora mínima debe ser menor que la máxima';
    if (maxConsecutive < 1) return 'Los mensajes consecutivos máximos deben ser mayor o igual a 1';
    if (maxAmountInConversation < 1) return 'El máximo de mensajes en la conversación debe ser mayor o igual a 1';
    if (clientsBatchSize !== null && clientsBatchSize < 1)
      return 'El límite de clientes por ejecución debe ser mayor o igual a 1';
    return null;
  })();

  const isCreateFollowUpDisabled =
    !newFollowUpConfig ||
    !selectedTemplate ||
    !outboundCampaignId ||
    !newFollowUpConfig.surveyConfig?.surveyName?.trim() ||
    !!numericRangeError ||
    (bodyComponentsLength ? bodyComponentsLength !== templateBodyParameters?.length : false);

  const getDisabledMessage = () => {
    if (!selectedTemplate) return 'No hay ninguna plantilla seleccionada';
    if (!outboundCampaignId) return 'No hay ningún identificador para la encuesta';
    if (!newFollowUpConfig.surveyConfig?.surveyName?.trim()) return 'Falta el nombre de la configuración de encuesta';
    if (numericRangeError) return numericRangeError;
    if (bodyComponentsLength && bodyComponentsLength !== templateBodyParameters?.length)
      return 'Falta seleccionar atributos para la plantilla';
    return null;
  };

  const handleCreateFollowUp = () => {
    if (isCreateFollowUpDisabled) return;
    createFollowUpConfig({
      ...newFollowUpConfig,
      conditionsToSend: JSON.parse(newFollowUpConfig.conditionsToSend) as string,
      templateComponents: newFollowUpConfig.templateComponents
        ? (JSON.parse(newFollowUpConfig.templateComponents) as string)
        : null,
    }).catch((err) => console.error(err));
  };

  const hasBodyParameters = !!selectedTemplate?.components.find(
    (c) => c.type === 'BODY' && (c.example?.body_text?.length || c.example?.body_text_named_params?.length)
  );

  const previewBodyVariableExamples =
    (templateBodyParameters?.length && templateBodyParameters.map((p) => p.value)) ||
    selectedTemplate?.components.find((c) => c.type === 'BODY')?.example?.body_text?.[0] ||
    selectedTemplate?.components.find((c) => c.type === 'BODY')?.example?.body_text_named_params ||
    null;

  return (
    <Box sx={{ width: '100%' }}>
      <AlertDialog
        openDialog={isError}
        setOpenDialog={setIsError}
        header={`Error al ${createError ? 'crear' : 'editar'} follow up`}
        msg={JSON.stringify(createError)}
        cancelMsg="Cerrar"
      />

      {/* ── Defaults info card ───────────────────────────────────────────── */}
      <Box
        sx={{
          ...surfaceCardSx,
          mb: sectionGap,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          backgroundColor: alpha(YELLOW, 0.08),
          boxShadow: 'none',
        }}
      >
        <InfoOutlinedIcon sx={{ color: NAVY, mt: 0.25 }} />
        <Box>
          <Typography sx={{ ...sectionTitleSx, fontSize: '1rem' }}>Cómo se enviará tu encuesta</Typography>
          <Typography sx={{ ...sectionSubtitleSx, mt: 0.5 }}>
            Se enviará a clientes creados en los últimos 3 meses que aún no la hayan recibido, entre las 9 y las 18
            horas de lunes a viernes, en lotes de 50 clientes cada 20 minutos. Para ajustar estos parámetros, contacta
            al equipo de Relif.
          </Typography>
        </Box>
      </Box>

      {/* ── Step 1: Choose template ─────────────────────────────────────── */}
      <Box sx={{ ...surfaceCardSx, mb: sectionGap }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Paso 1</Typography>
          <Typography sx={{ ...sectionTitleSx, mt: 0.5 }}>Elige una plantilla</Typography>
          <Typography sx={sectionSubtitleSx}>
            Selecciona una plantilla aprobada de WhatsApp o crea una nueva si ninguna se ajusta.
          </Typography>
        </Box>

        {isLoadingTemplates ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {templates.length === 0 && (
              <Alert severity="info" sx={{ mb: 2, fontFamily: 'Jost, sans-serif', borderRadius: RADIUS_ITEM }}>
                No hay plantillas disponibles para esta unidad de negocio. Crea una nueva para continuar.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {templates.length > 0 && (
                <Autocomplete
                  sx={{ flex: 1, minWidth: 240 }}
                  options={templates}
                  value={selectedTemplate}
                  onChange={(_, value) => setSelectedTemplate(value)}
                  getOptionLabel={(option) => option.name}
                  getOptionDisabled={(option) => !isTemplateSelectable(option.status)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} label="Plantilla" required />}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={option.id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
                    >
                      <span>{option.name}</span>
                      <TemplateStatusTag status={option.status} />
                    </Box>
                  )}
                  noOptionsText="Sin resultados"
                />
              )}
              <PrimaryBtn onClick={() => setOpenNewTemplate(true)} variant="contained" startIcon={<AddIcon />}>
                Crear nueva plantilla
              </PrimaryBtn>
            </Box>
          </>
        )}
      </Box>

      {/* ── Template create modal ───────────────────────────────────────── */}
      {openNewTemplate && (
        <Modal open onClose={() => setOpenNewTemplate(false)}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', md: '70%' },
              maxHeight: '90vh',
              overflow: 'auto',
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
              boxShadow: SHADOW,
              p: 4,
            }}
          >
            <CreateTemplateForm businessUnitId={businessUnitId} />
          </Paper>
        </Modal>
      )}

      {/* ── Steps 2+: Configure & preview ───────────────────────────────── */}
      {selectedTemplate && (
        <Grid container spacing={sectionGap} sx={{ alignItems: 'flex-start' }}>
          {/* Configuration column */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Box sx={{ ...surfaceCardSx, display: 'flex', flexDirection: 'column', gap: sectionGap }}>
              <Box>
                <Typography sx={labelSx}>Paso 2</Typography>
                <Typography sx={{ ...sectionTitleSx, mt: 0.5 }}>Identifica tu encuesta</Typography>
                <Typography sx={{ ...sectionSubtitleSx, mb: 1.5 }}>
                  Usa un nombre claro para reconocerla en reportes y métricas.
                </Typography>
                <TextField
                  label="Identificador"
                  placeholder="ej: lanzamiento-marzo-2026"
                  value={outboundCampaignId || ''}
                  onChange={(e) => setOutboundCampaignId(e.target.value)}
                  type="text"
                  required
                  fullWidth
                />
              </Box>

              {hasBodyParameters && (
                <>
                  <Divider sx={{ borderColor: alpha(NAVY, 0.06) }} />
                  <Box>
                    <Typography sx={labelSx}>Paso 3</Typography>
                    <Typography sx={{ ...sectionTitleSx, mt: 0.5 }}>Personaliza los parámetros</Typography>
                    <Typography sx={{ ...sectionSubtitleSx, mb: 1.5 }}>
                      Selecciona los atributos del cliente que reemplazarán los parámetros del cuerpo. Verás los cambios
                      en la vista previa.
                    </Typography>
                    <Autocomplete
                      multiple
                      id="template-components"
                      value={templateBodyParameters || []}
                      onChange={(_, value) => handleTemplateBodyParametersChange(value)}
                      getOptionLabel={(option) => option.title}
                      options={clientAttributes}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Atributos del cliente" />
                      )}
                    />
                  </Box>
                </>
              )}

              <Divider sx={{ borderColor: alpha(NAVY, 0.06) }} />
              <Box>
                <Typography sx={labelSx}>{hasBodyParameters ? 'Paso 4' : 'Paso 3'}</Typography>
                <Typography sx={{ ...sectionTitleSx, mt: 0.5 }}>Configura la encuesta</Typography>
                <Typography sx={{ ...sectionSubtitleSx, mb: 1.5 }}>
                  Define la encuesta que se creará automáticamente después de enviar el mensaje. El nombre es
                  obligatorio.
                </Typography>
                <SurveyConfigurationSection
                  followUpConfigDraft={newFollowUpConfig}
                  setFollowUpConfigDraft={setNewFollowUpConfig}
                  isEditing
                  required
                />
              </Box>

              {currentUser?.isAdmin && newFollowUpConfig && (
                <>
                  <Divider sx={{ borderColor: alpha(NAVY, 0.06) }} />
                  <Box>
                    <Typography sx={labelSx}>Configuración avanzada (admin)</Typography>
                    <Box sx={{ mt: 1 }}>
                      <FollowUpConfigFormAttributes
                        followUpConfigDraft={newFollowUpConfig}
                        setFollowUpConfigDraft={setNewFollowUpConfig}
                        isEditing
                        isCampaign
                        businessUnitId={businessUnitId}
                        hideSurveyConfig
                        hideIdentifier
                      />
                    </Box>
                  </Box>
                </>
              )}

              <Divider sx={{ borderColor: alpha(NAVY, 0.06) }} />

              {/* Submit footer */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {isCreateFollowUpDisabled ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <InfoOutlinedIcon fontSize="small" />
                    <Typography sx={{ ...bodyTextSx, color: 'error.main', fontSize: '0.85rem' }}>
                      {getDisabledMessage()}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                    <CheckCircleIcon fontSize="small" />
                    <Typography sx={{ ...bodyTextSx, color: 'success.main', fontSize: '0.85rem' }}>
                      Todo listo. Haz clic en "Crear encuesta" para continuar.
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <PrimaryBtn
                    onClick={handleCreateFollowUp}
                    startIcon={<RocketLaunchIcon />}
                    disabled={isCreateFollowUpDisabled}
                    variant="contained"
                  >
                    Crear encuesta
                  </PrimaryBtn>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Preview column */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 24 },
              }}
            >
              <Typography sx={{ ...labelSx, mb: 1 }}>Vista previa en vivo</Typography>
              <PreviewTemplate
                cardTitle={selectedTemplate.name}
                headerEnabled={selectedTemplate.components.some((c) => c.type === 'HEADER')}
                headerText={selectedTemplate.components.find((c) => c.type === 'HEADER')?.text || null}
                headerFormat={selectedTemplate.components.find((c) => c.type === 'HEADER')?.format || null}
                headerFileUrl={
                  selectedTemplate.components.find((c) => c.type === 'HEADER' && c.format === 'IMAGE')?.example
                    ?.header_handle?.[0] || null
                }
                bodyText={selectedTemplate.components.find((c) => c.type === 'BODY')?.text || ''}
                bodyVariableExamples={previewBodyVariableExamples}
                footerEnabled={selectedTemplate.components.some((c) => c.type === 'FOOTER')}
                footerText={selectedTemplate.components.find((c) => c.type === 'FOOTER')?.text || null}
                buttons={selectedTemplate.components.find((c) => c.type === 'BUTTONS')?.buttons || null}
              />
              <Typography sx={{ ...sectionSubtitleSx, mt: 1.5, fontSize: '0.8rem' }}>
                *Asegúrate de que si la plantilla tiene parámetros, solo estén en el cuerpo.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FollowUpConfigForm;
